import React, { useEffect, useState } from 'react';
import Select from 'react-select';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { Checkbox, CheckboxGroup } from '../../../components/ui/Checkbox';
import { getParentCategoryOptions } from '../../../utils/categoryDropdownOptions';
import { createCategory } from '../../../utils/categoryApi';

const ProductModal = ({ 
  isOpen, 
  onClose, 
  product = null, 
  onSave,
  mode = 'create',
  categories: propCategories = [],
  onShowSuccess, // callback for success popup
  isBulkMode = false,         // ✅ NEW PROP
  selectedProducts = [],      // ✅ NEW PROP
  refreshProducts             // ✅ NEW PROP
}) => {
  const [formData, setFormData] = useState({
    name: '',
    url: '',
    eventTypes: [],
    retryPolicy: {
      maxRetries: 3,
      retryDelay: 5000
    },
    headers: {},
    isActive: true,
    categoryId: ''
  });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [categories, setCategories] = useState(propCategories);
  const [categorySearch, setCategorySearch] = useState("");
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);

  useEffect(() => {
    if (product && mode === 'edit' && !isBulkMode) {
      setFormData({
        name: product?.name || '',
        url: product?.url || '',
        eventTypes: product?.eventTypes || [],
        retryPolicy: product?.retryPolicy || { maxRetries: 3, retryDelay: 5000 },
        headers: product?.headers || {},
        isActive: product?.status === 'active',
        categoryId: product?.categoryId || ''
      });
    } else {
      setFormData({
        name: '',
        url: '',
        eventTypes: [],
        retryPolicy: { maxRetries: 3, retryDelay: 5000 },
        headers: {},
        isActive: true,
        categoryId: ''
      });
    }
    setErrors({});
  }, [product, mode, isOpen, isBulkMode]);

  // --- Tree dropdown logic (same as CategoryModal) ---
  const buildCategoryTree = (flatCategories) => {
    const idToNode = {};
    flatCategories.forEach(cat => {
      idToNode[cat.id] = { ...cat, children: [] };
    });
    const tree = [];
    flatCategories.forEach(cat => {
      if (cat.parentId !== "0" && idToNode[cat.parentId]) {
        idToNode[cat.parentId].children.push(idToNode[cat.id]);
      } else {
        tree.push(idToNode[cat.id]);
      }
    });
    return tree;
  };

  // Recursively build dropdown options with indentation
  const getDropdownOptions = (tree, level = 1) => {
    let options = [];
    tree.forEach(node => {
      options.push({
        value: node.id,
        label: `${'— '.repeat(level - 1)}${node.name} (Level ${node.level})`,
        disabled: node.level !== node.levels // Now uses dynamic levels from API
      });
      if (node.children && node.children.length > 0) {
        options = options.concat(getDropdownOptions(node.children, level + 1));
      }
    });
    return options;
  };

  const categoryTree = buildCategoryTree(categories);
  const categoryOptions = getDropdownOptions(categoryTree);

  const reactSelectOptions = categoryOptions.map(opt => ({
    value: opt.value,
    label: opt.label,
    isDisabled: opt.disabled
  }));

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    if (errors?.[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  // 🔑 BULK + SINGLE MAPPING HANDLER
const handleMapCategory = async () => {
  if (!formData.categoryId) {
    alert('Please select a category');
    return;
  }

  setIsSubmitting(true);
  try {
    const token = localStorage.getItem('authToken');
    const userId = localStorage.getItem('_id');

    // ✅ Conditional base URL
    const baseUrl =
      userId === "56"
        ? import.meta.env.VITE_CATDEMO_BASE_URL
        : import.meta.env.VITE_CATPROD_BASE_URL;

    if (isBulkMode) {
      // 🔁 Loop over all selected products sequentially
      for (const productId of selectedProducts) {
        await fetch(`${baseUrl}/product_details/map_category`, {
          method: 'POST',
          headers: {
            'accept': '*/*',
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            product_id: productId,
            client_category_id: formData.categoryId
          })
        });
      }
      onShowSuccess?.('Bulk category mapping successful');
      await refreshProducts?.();
      onClose();
      return;
    }

    // 🔹 Single product mapping
    const response = await fetch(`${baseUrl}/product_details/map_category`, {
      method: 'POST',
      headers: {
        'accept': '*/*',
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        product_id: product?.product_id,
        client_category_id: formData.categoryId
      })
    });

    const result = await response.json();
    if (result.status === "success") {
      const message = result.message || 'Category Updated';
      onShowSuccess?.(message);
      await refreshProducts?.();
      onClose();
    } else {
      alert(result.message || 'Failed to map category');
    }
  } catch (error) {
    console.error('Error mapping category:', error);
    alert('Error mapping category');
  } finally {
    setIsSubmitting(false);
  }
};


  // Fetch categories when modal opens
useEffect(() => {
  if (isOpen) {
    const fetchCategories = async () => {
      try {
        const token = localStorage.getItem('authToken');
        const userId = localStorage.getItem('_id');

        // ✅ Conditional base URL
        const baseUrl =
          userId === "56"
            ? import.meta.env.VITE_CATDEMO_BASE_URL
            : import.meta.env.VITE_CATPROD_BASE_URL;

        const res = await fetch(`${baseUrl}/category/active`, {
          method: 'GET',
          headers: {
            'accept': 'application/json',
            'Authorization': `Bearer ${token}`
          }
        });

        if (!res.ok) {
          throw new Error(`Failed to fetch categories: ${res.status}`);
        }

        const data = await res.json();

        const mapped = Array.isArray(data)
          ? data.map(cat => ({
              id: String(cat.categoryId),
              name: cat.name,
              parentId: String(cat.parentInt),
              level: cat.level,
              levels: cat.levels,
              description: cat.description || ''
            }))
          : [];

        setCategories(mapped);
      } catch (error) {
        console.error('Error fetching categories:', error);
        setCategories([]);
      }
    };
    fetchCategories();
  }
}, [isOpen]);

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black bg-opacity-50 z-90" onClick={onClose} />
      {/* Modal */}
      <div className="fixed inset-0 z-100 overflow-y-auto">
        <div className="flex min-h-full items-center justify-center p-4">
          <div className="bg-card border border-border rounded-lg shadow-modal w-full max-w-2xl max-h-[90vh] overflow-hidden">
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-border">
              <div>
                <h2 className="text-lg font-semibold text-foreground">
                  {isBulkMode
                    ? 'Bulk Product Mapping'
                    : mode === 'edit'
                    ? 'Product Mapping'
                    : 'Create New Product'}
                </h2>
                <p className="text-sm text-muted-foreground mt-1">
                  {isBulkMode
                    ? 'Assign a category to multiple products at once'
                    : 'Configure product endpoint for real-time event notifications'}
                </p>
              </div>
              <Button
                variant="ghost"
                size="xs"
                iconName="X"
                onClick={onClose}
              />
            </div>

            {/* Content */}
            <form className="overflow-y-auto max-h-[calc(90vh-140px)]">
              <div className="p-6 space-y-6">
                {/* Category Dropdown */}
                <div className="mb-6">
                  <label className="block text-sm font-medium text-foreground mb-2">Category</label>
                  <Select
                    classNamePrefix="react-select"
                    options={reactSelectOptions}
                    value={reactSelectOptions.find(opt => opt.value === formData.categoryId) || null}
                    onChange={opt => handleInputChange('categoryId', opt ? opt.value : '')}
                    isOptionDisabled={opt => opt.isDisabled}
                    placeholder="Select or search category..."
                    isClearable
                  />
                </div>
              </div>

              {/* Footer */}
              <div className="flex items-center justify-end space-x-3 p-6 border-t border-border bg-muted/30">
                <Button
                  type="button"
                  variant="outline"
                  onClick={onClose}
                  disabled={isSubmitting}
                >
                  Cancel
                </Button>
                <Button
                  type="button"
                  disabled={isSubmitting}
                  onClick={handleMapCategory}
                >
                  {isSubmitting
                    ? 'Updating...'
                    : isBulkMode
                    ? 'Update All'
                    : 'Update Product'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </>
  );
};

export default ProductModal;
